import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, mergeProps as _mergeProps, toHandlers as _toHandlers, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-y-4" }
const _hoisted_2 = { class: "flex items-center justify-between" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("h2", { class: "text-lg font-bold text-left text-[#163D45]" }, "Informações básicas", -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("p", { class: "font-bold text-base text-[#323232] text-left mb-4 mt-4" }, "Regras de proporcionalidade", -1)
const _hoisted_5 = { class: "flex gap-6" }
const _hoisted_6 = /*#__PURE__*/_createElementVNode("p", { class: "font-bold text-base text-[#323232] text-left mb-2 mt-4" }, " Lembrete de confirmação pelo aplicativo ", -1)
const _hoisted_7 = { class: "flex gap-2" }

import { ref, onMounted, computed, inject, watch } from 'vue'
import { timezones } from '@/hooks/useCommon'
import SelectableCard from '@/components/designSystem/Cards/SelectableCard.vue'
import AgendaTypeField from '@/components/ManageScale/Schedule/AgendaTypeField.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import type {
  HealthUnitResponse,
  SimpleHealthUnitResponse
} from '@/Network/Types/Responses/HealthUnitResponses'
import HealthUnitModal from '@/modules/company/Units/modals/UnitFormModal.vue'
import { storeToRefs } from 'pinia'
import { useCreateScheduleStore } from '@/pinia-store/useCreateScheduleStore'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'
import { proportionalityRuleOptions } from '@/utils/schedule'
import FieldOptionsService from '@/Network/Services/FieldOptionsService'
import OperationalGroupSelect from '@/components/designSystem/Select/OperationalGroupSelect.vue'
import { mdiCalendarBlankOutline, mdiContentDuplicate } from '@mdi/js'
import { DatePicker } from 'v-calendar'
import 'v-calendar/style.css'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'


export default /*#__PURE__*/_defineComponent({
  __name: 'ScheduleBasicInfo',
  props: {
    errors: {},
    onClearError: { type: Function }
  },
  emits: ["openImportModal"],
  setup(__props: any, { emit }) {

const props = __props;

const store = (inject('createScheduleStore') ?? useCreateScheduleStore()) as ReturnType<
  typeof useCreateScheduleStore
>
const { step1 } = storeToRefs(store)
const { flags } = useFeatureFlags()

store.markAsDirty()

const operationModal = ref<'create'>('create')
const toggleHealthUnitModal = ref(false)
const healthUnitToUpdate = ref<SimpleHealthUnitResponse>()

const healthUnitStore = useHealthUnitStore()
const { healthUnitsAdminAccess, isLoadingSelectFetch } = storeToRefs(healthUnitStore)

const handleOpenModal = (operation: 'create') => {
  operationModal.value = operation
  toggleHealthUnitModal.value = true
}
const closeHealthUnitModal = async () => {
  toggleHealthUnitModal.value = false
}
const handleUnitCreated = async (newHealthUnit: HealthUnitResponse) => {
  await healthUnitStore.getHealthUnitOptions(true)
  step1.value.healthUnitId = newHealthUnit.id
  handleHealthUnitSelection(newHealthUnit.id)
}

const operationalGroupSelection = ref<{ id: number; name: string } | null>(null)
const allOperationalGroups = ref<{ id: number; name: string }[]>([])
const fieldOptionsService = new FieldOptionsService()

function updateOperationalGroup(selection: { id: number; name: string } | string | null) {
  let groupNameToSave: string | null = null
  let groupObjectToDisplay: { id: number; name: string } | null = null

  if (typeof selection === 'object' && selection !== null) {
    groupNameToSave = selection.name
    groupObjectToDisplay = selection
  } else if (typeof selection === 'string' && selection.length > 0) {
    groupNameToSave = selection
    groupObjectToDisplay = allOperationalGroups.value.find((g) => g.name === selection) || null
  }

  step1.value.operationalGroup = groupNameToSave
  operationalGroupSelection.value = groupObjectToDisplay
}

onMounted(async () => {
  if (!step1.value.timezone) {
    step1.value.timezone = '-03:00'
  }

  try {
    // O mapeamento do valor atual de step1.operationalGroup (nome) para o objeto
    // {id, name} que o OperationalGroupSelect precisa fica a cargo do watch abaixo —
    // ele reage à chegada de `allOperationalGroups` (esta linha) do mesmo jeito que
    // reage a mudanças programáticas de operationalGroup depois do mount.
    allOperationalGroups.value = await fieldOptionsService.getOperationalGroups()
  } catch (error) {
    console.error('Falha ao carregar grupos operacionais', error)
  }
})

/**
 * Mantém `operationalGroupSelection` (o objeto {id, name} que o
 * OperationalGroupSelect exibe) sincronizado com `step1.operationalGroup` (a
 * string que a store guarda).
 *
 * Sem este watch, preenchimento programático da store DEPOIS do mount (ex.: o
 * import de configurações de outra escala, que roda em resposta a um clique do
 * usuário, bem depois do onMounted) não passa por `updateOperationalGroup` — o
 * campo Grupo Operacional aparece vazio mesmo com valor salvo na store.
 *
 * Observar `allOperationalGroups` também cobre o caso em que a lista de grupos
 * ainda não tinha chegado quando `operationalGroup` mudou (o fetch acima é
 * assíncrono) — quando ela chega, a busca é refeita.
 *
 * Guard de igualdade evita ciclo: `updateOperationalGroup` já escreve os dois
 * refs juntos, então quando ESTE watch dispara em reação a essa escrita, o
 * valor já está correto e a atualização é pulada.
 */
watch([() => step1.value.operationalGroup, allOperationalGroups], ([groupName]) => {
  if (operationalGroupSelection.value?.name === groupName) return

  operationalGroupSelection.value = groupName
    ? (allOperationalGroups.value.find((g) => g.name === groupName) ?? null)
    : null
})

function handleHealthUnitSelection(selectedId: number | null) {
  props.onClearError('healthUnitId')
  if (!selectedId) {
    step1.value.healthUnitName = ''
    updateOperationalGroup(null)
    return
  }

  const selectedUnit = healthUnitsAdminAccess.value.find((unit) => unit.value === selectedId)
  step1.value.healthUnitName = selectedUnit?.title || ''

  updateOperationalGroup(selectedUnit?.operationalGroup || null)
}

const operationalGroupOptions = computed(() => {
  if (!step1.value.healthUnitId) {
    return []
  }
  return allOperationalGroups.value
})



// eslint-disable-next-line @typescript-eslint/no-unused-vars


return (_ctx: any,_cache: any) => {
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _hoisted_3,
        (_unref(flags).enabledPolicyModules)
          ? (_openBlock(), _createBlock(OutlineButton, {
              key: 0,
              size: "small",
              width: "auto",
              height: "32px",
              prependIcon: _unref(mdiContentDuplicate),
              "data-testid": "import-schedule-config-button",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('openImportModal')))
            }, {
              default: _withCtx(() => [
                _createTextVNode(" Importar configurações ")
              ]),
              _: 1
            }, 8, ["prependIcon"]))
          : _createCommentVNode("", true)
      ]),
      _createVNode(_component_v_row, {
        dense: "",
        class: "gap-4"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: _unref(step1).scheduleName,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(step1).scheduleName) = $event)),
                label: "Nome da Escala",
                variant: "outlined",
                color: "primary20",
                error: !!props.errors.scheduleName,
                "error-messages": props.errors.scheduleName,
                "hide-details": "auto",
                onInput: _cache[2] || (_cache[2] = ($event: any) => (props.onClearError('scheduleName')))
              }, null, 8, ["modelValue", "error", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_row, { dense: "" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, {
                cols: "12",
                md: "8"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_autocomplete, {
                    modelValue: _unref(step1).timezone,
                    "onUpdate:modelValue": [
                      _cache[3] || (_cache[3] = ($event: any) => ((_unref(step1).timezone) = $event)),
                      _cache[4] || (_cache[4] = ($event: any) => (props.onClearError('timezone')))
                    ],
                    label: "Selecione o fuso-horário",
                    items: _unref(timezones),
                    variant: "outlined",
                    color: "primary20",
                    error: !!props.errors.timezone,
                    "error-messages": props.errors.timezone,
                    "hide-details": "auto"
                  }, {
                    item: _withCtx(({ props, item }) => [
                      _createVNode(_component_v_list_item, _mergeProps(props, {
                        title: item.raw?.title,
                        subtitle: item.raw?.subtitle
                      }), null, 16, ["title", "subtitle"])
                    ]),
                    _: 1
                  }, 8, ["modelValue", "items", "error", "error-messages"])
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, {
                cols: "12",
                md: "4"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(DatePicker), {
                    modelValue: _unref(step1).endDate,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((_unref(step1).endDate) = $event)),
                    locale: "pt-BR",
                    masks: { input: `DD/MM/YYYY` }
                  }, {
                    default: _withCtx(({ inputValue, inputEvents }) => [
                      _createVNode(_component_v_text_field, _mergeProps({
                        label: "Final da escala",
                        "append-inner-icon": _unref(mdiCalendarBlankOutline),
                        "model-value": inputValue
                      }, _toHandlers(inputEvents), {
                        color: "primary20",
                        variant: "outlined",
                        "hide-details": "auto",
                        error: !!props.errors.endDate,
                        "error-messages": props.errors.endDate,
                        onFocus: _cache[5] || (_cache[5] = ($event: any) => (props.onClearError('endDate')))
                      }), null, 16, ["append-inner-icon", "model-value", "error", "error-messages"])
                    ]),
                    _: 1
                  }, 8, ["modelValue"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_autocomplete, {
                modelValue: _unref(step1).healthUnitId,
                "onUpdate:modelValue": [
                  _cache[7] || (_cache[7] = ($event: any) => ((_unref(step1).healthUnitId) = $event)),
                  handleHealthUnitSelection
                ],
                label: "Selecione a unidade vinculada",
                items: _unref(healthUnitsAdminAccess),
                loading: _unref(isLoadingSelectFetch),
                "no-data-text": "Nenhuma unidade encontrada",
                variant: "outlined",
                color: "primary20",
                error: !!props.errors.healthUnitId,
                "error-messages": props.errors.healthUnitId,
                "hide-details": "auto"
              }, {
                item: _withCtx(({ props, item }) => [
                  _createVNode(_component_v_list_item, _mergeProps(props, {
                    title: item.raw?.title,
                    subtitle: item.raw?.subtitle
                  }), null, 16, ["title", "subtitle"])
                ]),
                _: 1
              }, 8, ["modelValue", "items", "loading", "error", "error-messages"]),
              _createElementVNode("p", {
                class: "cursor-pointer text-left text-sm text-primary mb-2 mt-1",
                onClick: _cache[8] || (_cache[8] = ($event: any) => (handleOpenModal('create')))
              }, " Deseja cadastrar uma nova unidade? ")
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _createVNode(OperationalGroupSelect, {
                "model-value": operationalGroupSelection.value,
                "onUpdate:modelValue": _cache[9] || (_cache[9] = 
            (selection) => {
              props.onClearError('operationalGroup')
              updateOperationalGroup(selection)
            }
          ),
                disabled: !_unref(step1).healthUnitId,
                items: operationalGroupOptions.value,
                error: !!props.errors.operationalGroup,
                "error-messages": props.errors.operationalGroup
              }, null, 8, ["model-value", "disabled", "items", "error", "error-messages"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    (_unref(flags).enabledDashboardDemand)
      ? (_openBlock(), _createBlock(AgendaTypeField, {
          key: 0,
          modelValue: _unref(step1).agendaType,
          "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((_unref(step1).agendaType) = $event)),
          mode: "create",
          readonly: false,
          detection: null
        }, null, 8, ["modelValue"]))
      : _createCommentVNode("", true),
    _hoisted_4,
    _createElementVNode("div", _hoisted_5, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(proportionalityRuleOptions), (opt) => {
        return (_openBlock(), _createElementBlock("div", {
          key: opt.value,
          class: "flex-1"
        }, [
          _createVNode(SelectableCard, {
            title: opt.title,
            description: opt.description,
            selected: _unref(step1).proportionality === opt.value,
            error: !!props.errors.proportionality,
            errorMessage: props.errors.proportionality,
            onClick: 
          () => {
            _unref(step1).proportionality = opt.value
            props.onClearError('proportionality')
          }
        
          }, null, 8, ["title", "description", "selected", "error", "errorMessage", "onClick"])
        ]))
      }), 128))
    ]),
    _hoisted_6,
    _createElementVNode("div", _hoisted_7, [
      _createVNode(_component_v_checkbox, {
        modelValue: _unref(step1).teamDoctorReminderEnabled,
        "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((_unref(step1).teamDoctorReminderEnabled) = $event)),
        label: "Lembrete para médico interno",
        density: "compact",
        color: "primary",
        "hide-details": "auto",
        disabled: true,
        class: "w-1/2"
      }, null, 8, ["modelValue"]),
      _createVNode(_component_v_checkbox, {
        modelValue: _unref(step1).externalDoctorReminderEnabled,
        "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((_unref(step1).externalDoctorReminderEnabled) = $event)),
        label: "Lembrete para médico externo",
        density: "compact",
        color: "primary",
        "hide-details": "auto",
        class: "w-1/2"
      }, null, 8, ["modelValue"])
    ]),
    (toggleHealthUnitModal.value)
      ? (_openBlock(), _createBlock(HealthUnitModal, {
          key: 1,
          healthUnit: healthUnitToUpdate.value,
          modalStateProp: toggleHealthUnitModal.value,
          onClose: closeHealthUnitModal,
          onCreated: handleUnitCreated,
          type: operationModal.value
        }, null, 8, ["healthUnit", "modalStateProp", "type"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})